import { createFileRoute } from "@tanstack/react-router";
import { PeoplePage, usePeopleApi, type AppRole } from "@optymyse/suite-ui";
import { workspaceSlug } from "../lib/hosts";
import { peopleViewer, seesPeople, useWorkspace } from "../lib/workspaces";
import { useMe, meUser } from "../lib/auth";
import { strings } from "../lib/strings";
import { ApiError, askApi } from "../lib/api";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";

/** Steer's one role of its own for its Users (BUILD_SPEC §8.2): a User holds it or is a Viewer. */
const STEER_ROLES: readonly AppRole[] = [
  { key: "member", label: "Member", description: strings.workspaces.people.member },
];

type PeopleSearch = { tab?: "invitations" };

/**
 * The workspace's People: the suite's page (SuiteUi's PeoplePage), its lists and changes from the People API
 * (usePeopleApi, src/domains/people/routes.ts), asked through the page's own client, which notes the version that
 * answered (askApi). The organisation's owners and admins, and Steer's Administrators, choose who has Steer and who is
 * its Admin; Administrators make Users Members; everyone else sees why the page isn't theirs. The API and Impa check
 * every change again.
 */
function PeopleRoute() {
  const slug = workspaceSlug();
  const view = useWorkspace(slug);
  const me = useMe();
  const user = meUser(me);
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const viewer = peopleViewer(view.data, user?.id);
  const people = usePeopleApi({ enabled: seesPeople(viewer), fetch: askApi });

  if (view.isLoading || me.isLoading) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="space-y-3 py-12">
          <div className="h-8 w-48 animate-pulse rounded bg-[hsl(var(--muted))]" />
          <div className="h-40 animate-pulse rounded bg-[hsl(var(--muted))]" />
        </div>
      </div>
    );
  }
  if (!view.data || !viewer || view.error instanceof ApiError) return <ToWorkspaces />;
  const { workspace } = view.data;

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <PeoplePage
        app="Steer"
        organisation={{ name: workspace.name, owners: people.owners }}
        viewer={viewer}
        roles={STEER_ROLES}
        noRole="Viewer"
        noRoleDescription={strings.workspaces.people.viewer}
        people={people.people}
        invitations={people.invitations}
        onChange={people.onChange}
        tab={tab ?? "people"}
        onTabChange={(next) => void navigate({ search: next === "invitations" ? { tab: "invitations" } : {} })}
        description={strings.workspaces.people.description.replace("{name}", workspace.name)}
      />
    </div>
  );
}

export const Route = createFileRoute("/admin/settings/people")({
  validateSearch: (search: Record<string, unknown>): PeopleSearch =>
    search.tab === "invitations" ? { tab: "invitations" } : {},
  component: PeopleRoute,
});
